import { autoinject, computedFrom, transient } from "aurelia-framework";
import { EntityValidationsService } from "../../../../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../client-validation-system/validations.models";
import { Claims } from "../../../../../../constants/authentication/claims";
import { StepModel } from "../../../../../../models/basics/basics.models";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { RuleCollectionModel } from "../../../../../../models/rules/rules.models";
import { VettingType } from "../../../../../../models/vetting/vetting.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { RulesClientService } from "../../../../../../services/rules/rules-client.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { DataGrid } from "../../../../../components/data-grid/data-grid";

@autoinject
@transient()
export class AddRulesCollectionDialog {


    ruleCollectionIcons: { icon: string }[] = [
        { icon: 'vetting' },
        { icon: 'auto-screening' },
        { icon: 'anchor' },
        { icon: 'bookmark' },
        { icon: 'desktop' },
        { icon: 'pin' },
        { icon: 'screening'}
    ]

    ruleCollectionId: number;
    name: string;
    isActive: boolean = true;
    displayName: string;
    notes: string;
    isVettingRule: boolean = true;
    isAutoScreeningRule: boolean = false;
    isDueDiligenceRule: boolean = false;
    ruleCollectionUITemplateId: number;
    selectedIcon: { icon: string } = this.ruleCollectionIcons[0];
    ruleCollectionUITemplate: LookupValue;
    ruleCollectionUIInputTemplateId: number;
    ruleCollectionUIInputTemplate: LookupValue;
    ruleCollectionUIListTemplate: LookupValue;
    windwardClientId: string;
    windwardClientSecret: string;
    canManageWindwardCredential: boolean;

    ruleCollections: { name: string, displayName: string, ruleCollectionId: number, vettingTypeId: number }[] = [];
    filteredRuleCollections: { name: string, displayName: string, ruleCollectionId: number, vettingTypeId: number }[] = [];
    ruleConfigs: { ruleName: string, ruleCode: string, ruleConfigId: number, ruleCategoryId: number }[] = [];
    filteredRuleConfigs: { ruleCategoryId: number, ruleName: string, ruleCode: string, ruleConfigId: number }[] = [];

    selectedRuleCollections: { name: string, displayName: string, childRuleCollectionId: number, ruleCollectionRuleCollectionId: number, vettingTypeId: number }[] = [];
    selectedRuleConfigs: { ruleCategoryId: number, ruleName: string, ruleCode: string, ruleConfigId: number, ruleCollectionRuleConfigId: number, isCritical: boolean }[] = [];
    filteredSelectedRuleCollections: { name: string, displayName: string, childRuleCollectionId: number, ruleCollectionRuleCollectionId: number, vettingTypeId: number }[] = [];
    filteredSelectedRuleConfigs: { ruleCategoryId: number, ruleName: string, ruleCode: string, ruleConfigId: number, ruleCollectionRuleConfigId: number, isCritical: boolean }[] = [];

    public tabs: StepModel[] = [];
    public currentTab: StepModel;

    isReadOnly: boolean = true;
    isCritical: boolean = true;
    isSoft: boolean;
    isInternal: boolean;
    ruleCollectionPrompt: string;
    businessUnitRuleCollectionId: number;
    businessUnitId: number;
    dataGrid: DataGrid;


    showRuleCategoryFilter: boolean = false;
    selectedRuleCategories: number[];
    selectedFilter: { title: string, clear: () => void, apply: () => void };
    isMobileFilterOpen: boolean;

    @computedFrom("isVettingRule", "isAutoScreeningRule", "isDueDiligenceRule")
    get vettingTypeId(): number {
        return this.getVettingType();
    }

    @computedFrom("ruleCollectionUITemplateId", "ruleCollectionUITemplate")
    get uiTemplateUrl(): number {
        return this.ruleCollectionUITemplate && this.ruleCollectionUITemplate.additionalData? this.ruleCollectionUITemplate.additionalData.imageUrl: null;
    }

    @computedFrom("ruleCollectionUIInputTemplateId", "ruleCollectionUIInputTemplate")
    get uiInputTemplateUrl(): number {
        return this.ruleCollectionUIInputTemplate && this.ruleCollectionUIInputTemplate.additionalData? this.ruleCollectionUIInputTemplate.additionalData.imageUrl: null;
    }

    @computedFrom("ruleCollectionUIListTemplate", "ruleCollectionUIListTemplate.id")
    get uiListTemplateUrl(): number {
        return this.ruleCollectionUIListTemplate && this.ruleCollectionUIListTemplate.additionalData? this.ruleCollectionUIListTemplate.additionalData.imageUrl: null;
    }

    @computedFrom("hasRuleCategoryFilter")
    get hasAnyFilters(): boolean {
        return this.hasRuleCategoryFilter;
    }

    @computedFrom("currentTab", "currentTab.step")
    get hasRuleCategoryFilter(): boolean {
        return this.currentTab && this.currentTab.step == 2;
    }

    @computedFrom("selectedRuleCategories", "selectedRuleCategories.length")
    public get ruleCategoryFilterActive() {
        return this.selectedRuleCategories && this.selectedRuleCategories.length > 0;
    }

    public toggleRuleCategoryFilters() {
        if (!this.showRuleCategoryFilter) {
            this.closeAllFilters();
        }
        this.showRuleCategoryFilter = !this.showRuleCategoryFilter;
        this.selectedFilter = {
            title: "Rule Category",
            clear: () => {
                this.clearRuleCategoryFilter();
            },
            apply: () => {
                this.applyRuleCategoryFilter(this.selectedRuleCategories.map(cId => {
                    return { id: cId };
                }), false);
            }
        }
    }

    public clearRuleCategoryFilter() {
        this.selectedRuleCategories = [];
        this.filterItemSource();
    }

    public applyRuleCategoryFilter(items: any[], reload: boolean = true) {
        this.selectedRuleCategories = items.map(x => x.id);
        if (reload == true) this.filterItemSource();
        this.toggleRuleCategoryFilters();
    }

    public closeAllFilters() {
        this.showRuleCategoryFilter = false;
        return false;
    }

    public clearAllFilters(reload: boolean = true) {
        this.closeAllFilters();
        this.selectedRuleCategories = [];
        this._search = null;
        if (reload)
            this.filterItemSource();
        return false;
    }

    public get isSubFilterOpen(): boolean {
        return this.showRuleCategoryFilter;
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.showRuleCategoryFilter = false;
    }

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }

    hideMobileFilters() {
        this.isMobileFilterOpen = false;
    }

    public mobileFilterApply() {
        this.toggleMobileFilterDialog();
        this.filterItemSource();
    }

    constructor(private dialogClientService: DialogClientService, private rulesClientService: RulesClientService,
        private userClaimsService: UserClaimsService, private entityValidationsService: EntityValidationsService,
        private validationsClientService: ValidationsClientService, private notificationService: NotificationClientService) {
    }

    onSubmit: () => void;
    async activate(model: { ruleCollectionId: number, dataGrid: DataGrid, businessUnitId: number, onSubmit: (() => void) }) {
        const hasPermission = model && model.businessUnitId ? this.userClaimsService.hasPermission(Claims.CanAddRuleCollectionsAtBusinessUnit) : this.userClaimsService.hasPermission(Claims.CanAddRuleCollections);
        this.isReadOnly = model && model.ruleCollectionId && model.ruleCollectionId > 0 && !hasPermission;
        this.canManageWindwardCredential = this.userClaimsService.hasPermission(Claims.CanAddWindwardRuleCollectionCredential);
        this.dataGrid = model.dataGrid;
        this.businessUnitId = model.businessUnitId;
        this.ruleCollectionId = model.ruleCollectionId;
        this.onSubmit = model.onSubmit;
        this.ruleCollections = await this.rulesClientService.getRuleCollections();
        this.ruleConfigs = await this.rulesClientService.getRuleConfigs();
        if (model.ruleCollectionId) {
            const ruleCollection = await this.rulesClientService.getRuleCollection(model.ruleCollectionId);
            this.name = ruleCollection.name;
            this.isActive = ruleCollection.isActive;
            this.displayName = ruleCollection.displayName;
            this.notes = ruleCollection.notes;
            this.isVettingRule = ruleCollection.vettingTypeId == VettingType.FullVet;
            this.isAutoScreeningRule = ruleCollection.vettingTypeId == VettingType.AutoScreening;
            this.isDueDiligenceRule = ruleCollection.vettingTypeId == VettingType.DueDiligence;
            this.ruleCollectionUITemplateId = ruleCollection.ruleCollectionUITemplateId;
            this.ruleCollectionUIInputTemplateId = ruleCollection.ruleCollectionUIInputTemplateId;
            this.windwardClientId = ruleCollection.windwardClientId;
            this.windwardClientSecret = ruleCollection.windwardClientSecret;
            this.ruleCollectionPrompt = ruleCollection.ruleCollectionPrompt;
            this.ruleCollectionUIListTemplate = <LookupValue>{ id: ruleCollection.ruleCollectionUIListTemplateId };
            this.selectedIcon = this.ruleCollectionIcons.filter(x => x.icon == ruleCollection.icon)[0];
            const buRuleCollection = ruleCollection.businessUnitRuleCollections.filter(x => x.businessUnitId == this.businessUnitId)[0];
            if (buRuleCollection) {
                this.isInternal = buRuleCollection.isInternal;
                this.ruleCollectionPrompt = buRuleCollection.ruleCollectionPrompt;
                this.businessUnitRuleCollectionId = buRuleCollection.businessUnitRuleCollectionId;
            }
            this.selectedRuleConfigs = [];
            this.selectedRuleCollections = [];
            for (const rr of ruleCollection.ruleCollectionRuleConfigs) {
                const rrc = this.ruleConfigs.filter(x => x.ruleConfigId == rr.ruleConfigId)[0];
                if (rrc) {
                    this.selectedRuleConfigs.push({
                        ruleCategoryId: rrc.ruleCategoryId,
                        ruleConfigId: rr.ruleConfigId,
                        ruleCode: rrc.ruleCode,
                        ruleName: rrc.ruleName,
                        ruleCollectionRuleConfigId: rr.ruleCollectionRuleConfigId,
                        isCritical: rr.isCritical
                    });
                }
            }
            for (const r of ruleCollection.ruleCollectionRuleCollections) {
                const rc = this.ruleCollections.filter(x => x.ruleCollectionId == r.childRuleCollectionId)[0];
                if (rc) {
                    this.selectedRuleCollections.push({
                        childRuleCollectionId: rc.ruleCollectionId,
                        displayName: rc.displayName,
                        name: rc.name,
                        ruleCollectionRuleCollectionId: r.ruleCollectionRuleCollectionId,
                        vettingTypeId: rc.vettingTypeId
                    });
                }
            }
        }

        this.initializeTabs();
        this.filterItemSource();
    }

    close() {
        this.dialogClientService.hide();
    }

    @computedFrom("filteredSelectedRuleConfigs", "filteredSelectedRuleConfigs.length")
    get criticalRuleCount() {
        return this.selectedRuleConfigs?.filter(x => x.isCritical)?.length;

    }

    @computedFrom("filteredSelectedRuleConfigs", "filteredSelectedRuleConfigs.length")
    get softRuleCount() {
        return this.selectedRuleConfigs?.filter(x => !x.isCritical)?.length;
    }

    private initializeTabs() {
        const tabs: StepModel[] = [{
            title: 'Rules',
            step: 2,
            show: true,
        }, {
            title: 'Rule Collections',
            step: 1,
            show: true
        }];

        this.tabs = tabs;
        this.currentTab = this.tabs[0];
    }

    setSelectedIcon(icon: { icon: string }) {
        if (this.isReadOnly) return;
        this.selectedIcon = icon;
    }

    _search: string;
    set search(x: string) {
        this._search = x;
        this.filterItemSource();
    }

    @computedFrom("_search")
    get search() {
        return this._search;
    }

    screeningTypeChanged() {
        this.filterItemSource();
    }

    ruleCollectionTypeChanged() {
        this.filterItemSource();
    }

    getVettingType() {  
        return this.isVettingRule ? VettingType.FullVet : this.isAutoScreeningRule? VettingType.AutoScreening: VettingType.DueDiligence;
    }

    filterItemSource() {
        if (this.search) {
            this.filteredRuleCollections = this.ruleCollections.filter(x => (x.displayName && x.displayName.toLowerCase().indexOf(this.search.toLowerCase()) > -1) || (x.name && x.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1)).slice(0);
            this.filteredRuleConfigs = this.ruleConfigs.filter(x => (x.ruleName && x.ruleName.toLowerCase().indexOf(this.search.toLowerCase()) > -1) || (x.ruleCode && x.ruleCode.toLowerCase().indexOf(this.search.toLowerCase()) > -1)).slice(0);
            this.filteredSelectedRuleCollections = this.selectedRuleCollections.filter(x => (x.displayName && x.displayName.toLowerCase().indexOf(this.search.toLowerCase()) > -1) || (x.name && x.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1)).slice(0);
            this.filteredSelectedRuleConfigs = this.selectedRuleConfigs.filter(x => (x.ruleName && x.ruleName.toLowerCase().indexOf(this.search.toLowerCase()) > -1) || (x.ruleCode && x.ruleCode.toLowerCase().indexOf(this.search.toLowerCase()) > -1)).slice(0);
        }
        else {
            this.filteredRuleCollections = this.ruleCollections.slice(0);
            this.filteredRuleConfigs = this.ruleConfigs.slice(0);
            this.filteredSelectedRuleCollections = this.selectedRuleCollections.slice(0);
            this.filteredSelectedRuleConfigs = this.selectedRuleConfigs.slice(0);
        }

        let selectedRuleConfigs = this.filteredSelectedRuleConfigs;
        const selectedRuleCollections = this.filteredSelectedRuleCollections.filter(x => x.vettingTypeId == this.getVettingType());

        if (this.selectedRuleCategories && this.selectedRuleCategories.length > 0) {
            selectedRuleConfigs = selectedRuleConfigs.filter(x => this.selectedRuleCategories.indexOf(x.ruleCategoryId) > -1)
            this.filteredSelectedRuleConfigs = selectedRuleConfigs;
            this.filteredRuleConfigs = this.filteredRuleConfigs.filter(x => this.selectedRuleCategories.indexOf(x.ruleCategoryId) > -1)
        }

        if(this.vettingTypeId == VettingType.AutoScreening){
            this.filteredSelectedRuleConfigs = this.filteredSelectedRuleConfigs.filter(x => x.isCritical == this.isCritical);
        }

        const selectedRuleCollectionIds = selectedRuleCollections.map(x => x.childRuleCollectionId);
        const selectedRuleConfigIds = this.selectedRuleConfigs.map(x => x.ruleConfigId);
        this.filteredRuleCollections = this.filteredRuleCollections.filter(x => x.vettingTypeId == this.getVettingType())
            .filter(x => x.ruleCollectionId != this.ruleCollectionId && selectedRuleCollectionIds.indexOf(x.ruleCollectionId) == -1);
        this.filteredRuleConfigs = this.filteredRuleConfigs.filter(x => selectedRuleConfigIds.indexOf(x.ruleConfigId) == -1);
    }

    getValidator() {

        const rulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "RuleCollection",
            isCollection: false,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Name is required",
                parameters: null, property: "name",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "DisplayName is required",
                parameters: null, property: "displayName",
                validationType: ValidationTypeEnum.Required
            }]
        };

        return this.entityValidationsService.getValidator(this, rulesHolder);
    }

    async save() {
        if (this.isReadOnly) return;

        const ruleCollection: RuleCollectionModel = {
            isActive: this.isActive,
            ruleCollectionId: this.ruleCollectionId,
            displayName: this.displayName,
            name: this.name,
            notes: this.notes,
            businessUnitId: this.businessUnitId,
            icon: this.selectedIcon.icon,
            vettingTypeId: this.getVettingType(),
            ruleCollectionUITemplateId: this.ruleCollectionUITemplateId,
            ruleCollectionUIInputTemplateId: this.ruleCollectionUIInputTemplateId,
            ruleCollectionUIListTemplateId: this.ruleCollectionUIListTemplate? this.ruleCollectionUIListTemplate.id: null,
            ruleCollectionRuleCollections: [],
            ruleCollectionRuleConfigs: [],
            businessUnitRuleCollections: [],
            windwardClientId: this.windwardClientId,
            windwardClientSecret: this.windwardClientSecret,
            ruleCollectionPrompt: this.ruleCollectionPrompt
        };

        if (this.businessUnitId) {
            ruleCollection.businessUnitRuleCollections.push({
                businessUnitRuleCollectionId: this.businessUnitRuleCollectionId,
                businessUnitId: this.businessUnitId,
                isInternal: this.isInternal,
                ruleCollectionPrompt: this.ruleCollectionPrompt
            });
        }

        for (const r of this.selectedRuleConfigs) {
            ruleCollection.ruleCollectionRuleConfigs.push(r);
        }

        for (const r of this.selectedRuleCollections) {
            ruleCollection.ruleCollectionRuleCollections.push(r);
        }

        try {
            const validators = this.getValidator();
            const validationResponse = await validators.validate();
            if (validationResponse.valid) {
                try {
                    const response = await this.rulesClientService.saveRuleCollection(ruleCollection);
                    if (response.validationResult && response.validationResult.hasError) {
                        this.validationsClientService.showValidationErrors(response.validationResult);
                    } else {
                        if (this.dataGrid) {
                            this.dataGrid.reloadData();
                        }

                        this.notificationService.show(`Your rule has been ${this.ruleCollectionId > 0 ? 'saved' : 'added'} Successfully`, "", NotificationType.Success);
                        this.dialogClientService.hide();

                        if (this.onSubmit)
                            this.onSubmit();
                    }
                } catch (e) {
                    if (e.validationResult && e.validationResult.hasError) {
                        this.validationsClientService.showValidationErrors(e.validationResult);
                    }
                    throw e;
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    addAll() {
        if (this.currentTab) {
            if (this.currentTab.title == "Rule Collections") {
                for (const rc of this.filteredRuleCollections) {
                    this.selectedRuleCollections.push({
                        childRuleCollectionId: rc.ruleCollectionId,
                        displayName: rc.displayName,
                        name: rc.name,
                        ruleCollectionRuleCollectionId: 0,
                        vettingTypeId: rc.vettingTypeId
                    });
                }
            }
            else if (this.currentTab.title == "Rules") {
                for (const rc of this.filteredRuleConfigs) {
                    this.selectedRuleConfigs.push({
                        ruleCategoryId: rc.ruleCategoryId,
                        ruleConfigId: rc.ruleConfigId,
                        ruleCode: rc.ruleCode,
                        ruleName: rc.ruleName,
                        ruleCollectionRuleConfigId: 0,
                        isCritical: this.isCritical
                    });
                }
            }
            this.filterItemSource();
        }
    }

    removeAll() {
        if (this.currentTab) {
            if (this.currentTab.title == "Rule Collections") {
                for (const rc of this.filteredSelectedRuleCollections) {
                    const ruleCollectionId = rc.childRuleCollectionId;
                    const index = this.selectedRuleCollections.findIndex(x => x.childRuleCollectionId == ruleCollectionId);
                    this.selectedRuleCollections.splice(index, 1);
                }
            }
            else if (this.currentTab.title == "Rules") {
                for (const rc of this.filteredSelectedRuleConfigs) {
                    const ruleConfigId = rc.ruleConfigId;
                    const index = this.selectedRuleConfigs.findIndex(x => x.ruleConfigId == ruleConfigId);
                    this.selectedRuleConfigs.splice(index, 1);
                }
            }
            this.filterItemSource();
        }
    }

    addRuleCollection(rc: { ruleCollectionId: number, displayName: string, name: string, vettingTypeId: number }) {
        this.selectedRuleCollections.push({
            childRuleCollectionId: rc.ruleCollectionId,
            displayName: rc.displayName,
            name: rc.name,
            ruleCollectionRuleCollectionId: 0,
            vettingTypeId: rc.vettingTypeId
        });
        this.filterItemSource();
    }

    addRuleConfig(rc: { ruleConfigId: number, ruleCode: string, ruleName: string, ruleCategoryId: number }) {
        this.selectedRuleConfigs.push({
            ruleCategoryId: rc.ruleCategoryId,
            ruleConfigId: rc.ruleConfigId,
            ruleCode: rc.ruleCode,
            ruleName: rc.ruleName,
            ruleCollectionRuleConfigId: 0,
            isCritical: this.isCritical
        });
        this.filterItemSource();
    }

    removeRuleCollection(ruleCollectionId: number) {
        const index = this.selectedRuleCollections.findIndex(x => x.childRuleCollectionId == ruleCollectionId);
        this.selectedRuleCollections.splice(index, 1);
        this.filterItemSource();
    }

    removeRuleConfig(ruleConfigId: number) {
        const index = this.selectedRuleConfigs.findIndex(x => x.ruleConfigId == ruleConfigId);
        this.selectedRuleConfigs.splice(index, 1);
        this.filterItemSource();
    }
}
